import {
  useState,
  type ReactNode,
  type FC,
  type ReactElement,
  type MouseEvent,
} from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  styled,
  MenuItem,
  Menu,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  DataGridPro,
  type GridCellParams,
  type GridColDef,
} from '@mui/x-data-grid-pro';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PriceTooltip from '../home/status/PriceTooltipAsync';
import { useTranslation } from 'react-i18next';
import * as tripSlice from '../../data/trip.slice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useLoggedInProviderId } from '../../hooks/useLoggedInProviderId';
import type { TripLegInvoiceItem } from '../../data/models/trip-leg.model';
import { TripLegExceptionBC } from '../../data/models/trip-leg-exception.model';
import { selectTripLegExceptions } from '../../data/reference.slice';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchTripLegsV2Async } from '../../data/trip.slice';
import { tripStatusAttestationList } from '../../data/models/trip-leg-status.model';
import { attestationUtils } from '../../services/attestationUtils';
import { DateTime } from 'luxon';

const PREFIX = 'CollapsibleDataGrid';

const classes = {
  appBar: `${PREFIX}-appBar`,
  container: `${PREFIX}-container`,
  collapseText: `${PREFIX}-collapseText`,
  grid: `${PREFIX}-grid`,
  rowHover: `${PREFIX}-rowHover`,
  gridContainer: `${PREFIX}-ridContainer`,
  cellContent: `${PREFIX}-cellContent`,
  collapseContainer: `${PREFIX}-collapseContainer`,
  primaryText: `${PREFIX}-primaryText`,
  secondaryText: `${PREFIX}-secondaryText`,
  toolInput: `${PREFIX}-toolInput`,
  toolInputFieldset: `${PREFIX}-toolInputFieldset`,
  toolInputSelect: `${PREFIX}-toolInputSelect`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  memberName: `${PREFIX}-memberName`,
};

const StyledCollapseTable = styled(Box)(({ theme }) => ({
  [`& .${classes.appBar}`]: {
    background:
      'linear-gradient(0deg, rgba(1, 16, 18, 0.04) 0%, rgba(1, 16, 18, 0.04) 100%), #FFF',
    padding: '48px 0px 24px 0px',
    position: 'relative',
    boxShadow: 'none',
  },
  [`& .${classes.collapseText}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
  },
  [`& .${classes.collapseContainer}`]: {
    position: 'relative',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',

    '& .MuiDataGrid-row': {
      letterSpacing: '0.17px',
    },

    '& .MuiDataGrid-columnHeaders': {
      marginBottom: '8px',
    },

    '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContent, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitle':
      {
        width: '100%',
        color: 'rgba(1, 16, 18, 0.90)',

        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '24px',
        letterSpacing: '0.17px',
      },

    '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeader[data-field="member"] .MuiDataGrid-columnSeparator, & .MuiDataGrid-columnHeader[data-field="member"] .MuiDataGrid-iconSeparator':
      {
        display: 'flex !important',
        visibility: 'visible !important',
        opacity: '1 !important',
      },
    '& .MuiDataGrid-columnHeader[data-field="member"]:hover .MuiDataGrid-columnSeparator':
      {
        color: 'rgba(1, 16, 18, 0.90)',
      },

    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },

    '& .MuiDataGrid-cell': {
      '& .MuiButtonBase-root': {
        padding: '8px',
        marginLeft: '-6px',
        '& .MuiSvgIcon-root': {
          width: '20px !important',
          height: '20px !important',
        },
      },
    },

    '& .MuiDataGrid-cell:nth-last-child(2)': {
      flex: 1,
    },

    [`& .MuiDataGrid-row:not(:hover):not(.${classes.rowHover})`]: {
      '& .MuiDataGrid-cell:last-child': {
        position: 'absolute',
        right: 0,
        opacity: 0,
      },

      [`& .${classes.cellContent} input::placeholder`]: {
        color: 'transparent',
      },
      [`& .${classes.cellContent} fieldset`]: {
        display: 'none',
      },
    },

    [`& .MuiDataGrid-row.${classes.rowHover}, & .MuiDataGrid-row:hover`]: {
      background:
        'linear-gradient(0deg, rgba(1, 16, 18, 0.04) 0%, rgba(1, 16, 18, 0.04) 100%), #FFF',
    },

    [`& .MuiDataGrid-row:last-child .MuiDataGrid-cell`]: {
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },

    '& .MuiDataGrid-row:focus-within': {
      [`& .${classes.cellContent} input::placeholder`]: {
        color: 'rgba(1, 16, 18, 0.60)',
        opacity: 1,
      },
      [`& .${classes.cellContent} fieldset`]: {
        display: 'inherit',
      },
    },

    [`& .${classes.cellContent} input`]: {
      padding: '8px',
      textAlign: 'center',
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
  },
  [`& .${classes.primaryText}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  [`& .${classes.cellContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
  },
  [`& .${classes.secondaryText}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
  },
  [`& .${classes.memberName}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  [`& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-input`]:
    {
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '166%',
      letterSpacing: '0.4px',
    },

  [`& .MuiTablePagination-selectLabel`]: {
    color: 'rgba(1, 16, 18, 0.60)',
  },
  [`& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows`]: {
    fontFamily: 'Source Sans Pro',
  },
  [`& .MuiTablePagination-input`]: {
    fontFamily: 'Roboto',

    [`& .MuiSelect-select`]: {
      paddingBottom: 0,
    },
  },
  [`& .${classes.toolInput}`]: {
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: '#FFF',
    boxShadow: '0px 2px 7px 0px rgba(1, 16, 18, 0.15)',
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} input`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    padding: '12px',
    paddingRight: 0,

    '&::placeholder': {
      color: 'rgba(1, 16, 18, 0.60)',
    },
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: '12px',
    color: 'rgba(1, 16, 18, 0.60)',
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',

    [`& input.MuiInputBase-input::placeholder`]: {
      color: 'rgba(1, 16, 18, 0.60)',
      opacity: 1,
    },
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: '16px',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  [`& .MuiPaper-root`]: {
    width: '201px',
    marginLeft: '10px',
    borderRadius: '3px',
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

    [`& .MuiList-root`]: {
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      alignSelf: 'stretch',
    },
    [`& .MuiMenuItem-root`]: {
      color: 'rgba(1, 16, 18, 0.60)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
      padding: '10px 16px',
    },
  },
}));

interface CollapsibleDataGridProps {
  title: string;
  hideMoveToNeedsAttention: boolean;
  rows: TripLegInvoiceItem[];
  hidePriceBreakdown?: boolean;
}

const CollapsibleDataGrid: FC<CollapsibleDataGridProps> = ({
  title,
  hideMoveToNeedsAttention,
  rows,
  hidePriceBreakdown = false,
}) => {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOpenRowId, setMenuOpenRowId] = useState<number | null>(null);
  const providerId = useLoggedInProviderId();
  const dispatch = useAppDispatch();

  const tripLegExceptionItems = useAppSelector(selectTripLegExceptions);

  const openMenu = (event: MouseEvent<HTMLElement>, rowId: number) => {
    setAnchorEl(event.currentTarget);
    setMenuOpenRowId(rowId);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuOpenRowId(null);
  };

  const toggleCollapse = () => {
    setExpanded((prev) => !prev);
  };

  const moveToNeedsAttention = (tripLegID: number) => {
    const exception = tripLegExceptionItems.find(
      (item) => item.tripLegExceptionBC === TripLegExceptionBC.ReadyInvoice,
    );
    if (exception) {
      dispatch(
        tripSlice.setExceptions(
          [tripLegID],
          providerId,
          exception?.tripLegExceptionID,
          () => {
            if (providerId) {
              dispatch(
                fetchTripLegsV2Async({
                  transportationProviderID: [providerId.toString()],
                  attested: false,
                  includeTripLegAddOnLineItems: true,
                  page: 1,
                  pageSize: 1000,
                  tripLegStatusBCs: [...tripStatusAttestationList],
                  pickupStartDate: attestationUtils
                    .getStartDate()
                    .toUTC()
                    .toISO(),
                  pickupEndDate: DateTime.now().endOf('day').toUTC().toISO(),
                  includeTripLegStatusLogs: true,
                }),
              );
            }
          },
        ),
      );
    }
  };

  const renderPickupTime = (params: GridCellParams): ReactElement => {
    const { row } = params;
    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {row.pickupTime}
        </Typography>
        {row.isWillCall && (
          <Typography className={classes.primaryText}>
            {t('needsAttention.willCall')}
          </Typography>
        )}
        <Typography className={classes.secondaryText}>{row.tripID}</Typography>
      </Box>
    );
  };

  const renderPickupAddress = (params: GridCellParams): ReactElement => {
    const { row } = params;

    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {row.pickupAddress}
        </Typography>
        <Typography className={classes.secondaryText}>
          {row.pickupCity}
        </Typography>
      </Box>
    );
  };

  const renderDropoffAddress = (params: GridCellParams): ReactElement => {
    const { row } = params;
    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {row.dropoffAddress}
        </Typography>
        <Typography className={classes.secondaryText}>
          {row.dropoffCity}
        </Typography>
      </Box>
    );
  };

  const renderMember = (params: GridCellParams): ReactElement => {
    const { row } = params;
    return (
      <Box className={classes.cellContent}>
        <Typography className={`${classes.primaryText} ${classes.memberName}`}>
          {row.memberName}
        </Typography>
        {/* <Typography className={classes.secondaryText}>
          {row.memberId}
        </Typography> */}
      </Box>
    );
  };

  const renderDriver = (params: GridCellParams): ReactElement => {
    const { row } = params;
    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {row.driverName}
        </Typography>
      </Box>
    );
  };

  const renderTime = (params: GridCellParams): ReactElement => {
    const { row, field } = params;
    if (row.tripLegStatus === 'NOLOAD') {
      if (field === 'onBoard') {
        return (
          <Box className={classes.cellContent}>
            <Typography className={classes.primaryText}>
              {t('tripLegStatus.noLoad')}
            </Typography>
            <Typography className={classes.secondaryText}>
              {row.noLoad}
            </Typography>
          </Box>
        );
      }
    }

    if (row.tripLegStatus === 'CANCELEDLATE') {
      if (field === 'onSite') {
        return (
          <Box className={classes.cellContent}>
            <Typography className={classes.primaryText}>
              {t('tripLegStatus.canceledLate')}
            </Typography>
            <Typography className={classes.secondaryText}>
              {row.canceledLate}
            </Typography>
          </Box>
        );
      }
    }

    const isDisabled = (): boolean => {
      if (field === 'completed' && (row.onSite === '' || row.onBoard === '')) {
        return true;
      }
      if (field === 'onBoard' && row.onSite === '') {
        return true;
      }
      return false;
    };

    if (field === 'completed' && (row.canceledLate || row.noLoad)) {
      return <></>;
    }

    if (field === 'onBoard' && row.canceledLate) {
      return <></>;
    }

    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {String(params.value)}
        </Typography>
      </Box>
    );
  };

  const renderCenterHeader = ({
    colDef: { headerName },
  }: {
    colDef: { headerName?: string };
  }): ReactNode => {
    return (
      <Box
        style={{
          textAlign: 'center',
          width: '100%',
          lineHeight: '100%',
          height: '1',
        }}>
        {headerName}
      </Box>
    );
  };

  const renderPrice = (params: GridCellParams): ReactElement => {
    if (hidePriceBreakdown) {
      return (
        <Box className={classes.cellContent}>
          <Typography className={classes.primaryText}>
            {params.row.price}
          </Typography>
        </Box>
      );
    }
    return <PriceTooltip params={params as any} classes={classes} />;
  };

  const columns: GridColDef[] = [
    {
      field: 'pickupTime',
      headerName: t('generateInvoice.pickup'),
      width: 88,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('generateInvoice.pickupAddress'),
      width: 143,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('generateInvoice.dropoffAddress'),
      width: 143,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'member',
      headerName: t('generateInvoice.member'),
      renderCell: renderMember,
      width: 120,
      resizable: true,
    },
    {
      field: 'driverName',
      headerName: t('generateInvoice.driver'),
      renderCell: renderDriver,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'onSite',
      headerName: t('tripLegStatus.onSite'),
      renderCell: renderTime,
      flex: 0.7,
      minWidth: 88,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'onBoard',
      headerName: t('tripLegStatus.onBoard'),
      renderCell: renderTime,
      flex: 0.7,
      minWidth: 88,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'completed',
      headerName: t('tripLegStatus.completed'),
      renderCell: renderTime,
      flex: 0.7,
      minWidth: 88,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
    },
    {
      field: 'distance',
      headerName: t('generateInvoice.distance'),
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      flex: 0.6,
      minWidth: 72,
    },
    {
      field: 'price',
      headerName: t('generateInvoice.price'),
      flex: 0.6,
      minWidth: 70,
      renderCell: renderPrice,
      renderHeader: (params) => renderCenterHeader(params),
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 40,
      sortable: false,
      renderCell: (params) => (
        <>
          {hideMoveToNeedsAttention && (
            <>
              <IconButton onClick={(e) => openMenu(e, params.row.id)}>
                <MoreVertIcon />
              </IconButton>
              <StyledMenu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && menuOpenRowId === params.row.id}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                onClose={closeMenu}
                elevation={1}>
                <MenuItem
                  onClick={() => {
                    moveToNeedsAttention(params.row.id);
                    closeMenu();
                  }}>
                  {t('generateInvoice.moveToNeedsAttention')}
                </MenuItem>
              </StyledMenu>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <StyledCollapseTable className={classes.appBar}>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          marginLeft: '-29px',
        }}>
        <IconButton onClick={toggleCollapse} size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Typography variant="h6" className={classes.collapseText}>
          {title}
        </Typography>
      </Box>
      <Collapse in={expanded} className={classes.collapseContainer}>
        <DataGridPro
          rows={rows}
          columns={columns}
          className={classes.grid}
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          hideFooterPagination
          hideFooter
          rowHeight={56}
          disableColumnMenu={true}
          disableColumnSorting={true}
          getRowClassName={(params) => {
            if (params.row.id === menuOpenRowId) {
              return classes.rowHover;
            }
            return '';
          }}
        />
      </Collapse>
    </StyledCollapseTable>
  );
};

export default CollapsibleDataGrid;
